import { FunctionComponent, memo, useCallback, useContext, useMemo } from 'react';
import { useCookies } from 'react-cookie';
import { useTranslation } from 'react-i18next';
import sha1 from 'sha1';
import { AudioPlayerContext, ConfigContext, UserContext } from '../../../../context';
import { AUDIO_PLAYER_COOKIE_KEY } from '../../../constants';
import { useFreeLoginPopup } from '../../../hooks';
import { cookieOptions, durationFormatter, getCssVar } from '../../../utils';
import { IconLabel } from '../../IconLabel';

import classes from './AudioPlayerTrigger.module.scss';

interface AudioPlayerTriggerProps {
  storyId: number;
  cardId: number;
  cardTitle: string;
  chatId?: number;
  blockId?: string;
  src?: string;
  caption?: string;
}

export const AudioPlayerTrigger: FunctionComponent<AudioPlayerTriggerProps> = memo(
  ({ storyId, cardId, cardTitle, chatId, blockId, src, caption }) => {
    const { t } = useTranslation();

    const { audioPlayerData, setAudioPlayerData } = useContext(AudioPlayerContext);

    const { freeLoginModal, freeLoginHandler } = useFreeLoginPopup();

    const { userInfo, userProfile } = useContext(UserContext);

    const { id: userId, channelId } = userInfo.userData;

    const { isAnonymous } = userProfile;

    const { actionsDisabled = true } = { ...useContext(ConfigContext).config.elements.freeLogin };

    const { cardId: audioCardId, isPlaying: isAudioPlaying, src: audioSrc } = audioPlayerData;

    const [cookies, setCookie] = useCookies([AUDIO_PLAYER_COOKIE_KEY]);

    const { playlist = {} } = { ...cookies[AUDIO_PLAYER_COOKIE_KEY] };

    const track = useMemo(() => playlist[`${cardId}_${src}`], [cardId, playlist, src]);

    const { currentTime: trackCurrentTime = 0, played: trackPlayed = false } = { ...track };

    const onPlay = useCallback(() => {
      if (!src) {
        return;
      }

      if (isAnonymous && actionsDisabled) {
        freeLoginHandler();
        return;
      }

      setAudioPlayerData({
        ...audioPlayerData,
        userId: sha1(`${userId}`),
        channelId,
        storyId,
        cardId,
        cardTitle,
        chatId,
        blockId,
        src,
        isPlaying: true,
      });
    }, [
      actionsDisabled,
      blockId,
      cardId,
      cardTitle,
      channelId,
      chatId,
      freeLoginHandler,
      isAnonymous,
      audioPlayerData,
      setAudioPlayerData,
      src,
      storyId,
      userId,
    ]);

    const onPause = useCallback(() => {
      setAudioPlayerData({ ...audioPlayerData, isPlaying: false });
    }, [audioPlayerData, setAudioPlayerData]);

    const isPlaying = useMemo(
      () => isAudioPlaying && audioCardId === cardId && audioSrc === src,
      [audioCardId, audioSrc, cardId, isAudioPlaying, src]
    );

    const reset = useMemo(
      () => Boolean(!isPlaying && trackCurrentTime && !trackPlayed),
      [isPlaying, trackCurrentTime, trackPlayed]
    );

    const onClickHander = useCallback(() => {
      isPlaying ? onPause() : onPlay();
    }, [isPlaying, onPause, onPlay]);

    const resetHandler = useCallback(() => {
      playlist[`${cardId}_${src}`] = { played: false, currentTime: 0 };

      setCookie(AUDIO_PLAYER_COOKIE_KEY, { playlist }, cookieOptions());

      onPlay();
    }, [cardId, onPlay, playlist, setCookie, src]);

    const label = useMemo(() => {
      if (isPlaying) {
        return t('musicPlayer.playing');
      }

      switch (true) {
        case Boolean(trackPlayed):
          return t('musicPlayer.played');
        case Boolean(trackCurrentTime):
          return `${t('musicPlayer.continue-at')} ${durationFormatter(trackCurrentTime)}`;
        default:
          return t('musicPlayer.play');
      }
    }, [isPlaying, t, trackCurrentTime, trackPlayed]);

    if (!src) {
      return null;
    }

    return (
      <>
        <div className={classes['trigger']}>
          <IconLabel
            iconId={`${isPlaying ? 'pause' : 'play'}`}
            iconSize={40}
            color={getCssVar('--media-audio-icon-color')}
            onClick={onClickHander}
            label={label}
            singleColor
          />

          {reset && (
            <IconLabel
              iconId={'reset'}
              ariaLabel={t('common.play-from-start')}
              className={classes['trigger__reset']}
              color={getCssVar('--media-audio-icon-color')}
              onClick={resetHandler}
              singleColor
            />
          )}
        </div>
        {caption && <div className={classes['caption']}>{caption}</div>}

        {freeLoginModal}
      </>
    );
  }
);
